import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import {
  Dialog,
  DialogActionFooter,
  DialogCloseButton,
  DialogCloseIcon,
  DialogContent,
  DialogHeader,
  DialogSearchableMultiSelect,
  type DialogSearchableMultiSelectOption,
  DialogTitle,
} from 'components/ui';
import {
  FirmDocumentFolderCreateDialogField,
  FirmDocumentFolderCreateDialogFieldGroup,
  FirmDocumentFolderCreateDialogLabel,
} from 'pages/Documents/FirmDocuments/components/FirmDocumentFolderCreateDialog/FirmDocumentFolderCreateDialog.styles';
import {
  FirmDocumentEditDialogFileExtension,
  FirmDocumentEditDialogFileNameInput,
  FirmDocumentEditDialogFileNameInputShell,
} from './FirmDocumentEditDialog.styles';
import { FIRM_DOCUMENT_EDIT_DIALOG_TEST_IDS } from './FirmDocumentEditDialog.testIds';
import type {
  FirmDocumentEditDialogProps,
  FirmDocumentEditDialogValues,
  FirmDocumentReferenceObjectType,
} from './FirmDocumentEditDialog.types';
import {
  getEditableFileName,
  getFileExtension,
  getInitialReferenceObjectTypes,
  getInitialRequestedFileIds,
  LONG_FILE_EXTENSION_CHARACTER_COUNT,
} from './FirmDocumentEditDialog.utils';

const DOCUMENT_NAME_INPUT_ID = 'firm-document-edit-dialog-document-name';

const FirmDocumentEditDialog = ({
  documentReference,
  isLoadingRequestedFiles = false,
  isSaving = false,
  onClose,
  onConfirm,
  open,
  referenceOptions,
  requestedFileOptions,
}: FirmDocumentEditDialogProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [fileName, setFileName] = useState('');
  const [referenceObjectTypes, setReferenceObjectTypes] = useState<string[]>([]);
  const [requestedFileIds, setRequestedFileIds] = useState<number[]>([]);
  const trimmedFileName = fileName.trim();
  const canSave = Boolean(documentReference && trimmedFileName && !isSaving);
  const fileExtension = getFileExtension(documentReference);
  const hasLongFileExtension = fileExtension.length > LONG_FILE_EXTENSION_CHARACTER_COUNT;
  const referenceSelectOptions = useMemo<DialogSearchableMultiSelectOption[]>(
    () => referenceOptions.map(option => ({
      id: option.value,
      label: option.label,
    })),
    [referenceOptions]
  );
  const requestedFileSelectOptions = useMemo<DialogSearchableMultiSelectOption[]>(
    () => requestedFileOptions.map(option => ({
      id: option.id,
      label: option.name,
    })),
    [requestedFileOptions]
  );
  const supportedReferenceObjectTypes = useMemo(
    () => new Set(referenceSelectOptions.map(option => option.id)),
    [referenceSelectOptions]
  );

  const handleConfirm = useCallback(() => {
    if (!canSave) {
      return;
    }

    const validReferenceObjectTypes = referenceObjectTypes.filter(
      (referenceObjectType): referenceObjectType is FirmDocumentReferenceObjectType => (
        supportedReferenceObjectTypes.has(referenceObjectType)
      )
    );

    onConfirm({
      fileName: trimmedFileName,
      referenceObjectTypes: validReferenceObjectTypes,
      requestedFileIds,
    });
  }, [canSave, onConfirm, referenceObjectTypes, requestedFileIds, supportedReferenceObjectTypes, trimmedFileName]);

  const handleNameKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleConfirm();
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  };

  // Reset the editable document form from the selected file every time the modal opens.
  useEffect(() => {
    if (!open || !documentReference) {
      return;
    }

    setFileName(getEditableFileName(documentReference));
    setReferenceObjectTypes(getInitialReferenceObjectTypes(documentReference, referenceOptions));
    setRequestedFileIds(getInitialRequestedFileIds(documentReference));
  }, [documentReference, open, referenceOptions]);

  // Put the caret in the document name input so rename flows are keyboard-first.
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const focusTimer = window.setTimeout(() => inputRef.current?.focus());

    return () => window.clearTimeout(focusTimer);
  }, [open]);

  return (
    <Dialog
      data-testid={FIRM_DOCUMENT_EDIT_DIALOG_TEST_IDS.dialog}
      fullWidth
      maxWidth="xs"
      onClose={isSaving ? undefined : onClose}
      open={open}>
      <DialogHeader>
        <DialogTitle>Edit Document</DialogTitle>
        <DialogCloseButton
          aria-label="Close edit document dialog"
          data-testid={FIRM_DOCUMENT_EDIT_DIALOG_TEST_IDS.closeButton}
          disabled={isSaving}
          onClick={onClose}
          size="small">
          <DialogCloseIcon />
        </DialogCloseButton>
      </DialogHeader>
      <DialogContent>
        <FirmDocumentFolderCreateDialogFieldGroup>
          <FirmDocumentFolderCreateDialogField>
            <FirmDocumentFolderCreateDialogLabel htmlFor={DOCUMENT_NAME_INPUT_ID}>
              Document Name
            </FirmDocumentFolderCreateDialogLabel>
            <FirmDocumentEditDialogFileNameInputShell>
              <FirmDocumentEditDialogFileNameInput
                data-testid={FIRM_DOCUMENT_EDIT_DIALOG_TEST_IDS.documentNameInput}
                disabled={isSaving}
                hasExtension={Boolean(fileExtension)}
                hasLongExtension={hasLongFileExtension}
                id={DOCUMENT_NAME_INPUT_ID}
                inputRef={inputRef}
                onChange={event => setFileName(event.target.value)}
                onKeyDown={handleNameKeyDown}
                placeholder="Enter document name"
                value={fileName}
              />
              {fileExtension ? (
                <FirmDocumentEditDialogFileExtension hasLongExtension={hasLongFileExtension}>
                  {fileExtension}
                </FirmDocumentEditDialogFileExtension>
              ) : null}
            </FirmDocumentEditDialogFileNameInputShell>
          </FirmDocumentFolderCreateDialogField>
          <DialogSearchableMultiSelect
            clearSearchAriaLabel="Clear document reference search"
            clearSelectedAriaLabel="Clear document references"
            clearSelectedTestId={FIRM_DOCUMENT_EDIT_DIALOG_TEST_IDS.referenceClear}
            disabled={isSaving}
            emptyLabel="No reference types found"
            itemLabelPlural="reference types"
            itemLabelSingular="reference type"
            label="Document References"
            menuTestId={FIRM_DOCUMENT_EDIT_DIALOG_TEST_IDS.referenceMenu}
            onChange={ids => setReferenceObjectTypes(ids.filter((id): id is string => typeof id === 'string'))}
            options={referenceSelectOptions}
            placeholder="Select Document References"
            searchPlaceholder="Find a Reference"
            searchTestId={FIRM_DOCUMENT_EDIT_DIALOG_TEST_IDS.referenceSearch}
            selectTestId={FIRM_DOCUMENT_EDIT_DIALOG_TEST_IDS.referenceSelect}
            selectedPlacement="field"
            selectedIds={referenceObjectTypes}
            selectedOptionTestId={id => FIRM_DOCUMENT_EDIT_DIALOG_TEST_IDS.referenceSelectedOption(String(id))}
            testIdForOption={id => FIRM_DOCUMENT_EDIT_DIALOG_TEST_IDS.referenceOption(String(id))}
            triggerVariant="select"
          />
          <DialogSearchableMultiSelect
            addButtonLabel="Link to Requests"
            clearSearchAriaLabel="Clear linked request search"
            disabled={isSaving || isLoadingRequestedFiles}
            emptyLabel={
              isLoadingRequestedFiles
                ? 'Loading requests...'
                : 'No requests available for this company and date.'
            }
            isLoading={isLoadingRequestedFiles}
            itemLabelPlural="requests"
            itemLabelSingular="request"
            label="Linked Requests"
            menuTestId={FIRM_DOCUMENT_EDIT_DIALOG_TEST_IDS.requestMenu}
            onChange={ids => setRequestedFileIds(ids.filter((id): id is number => typeof id === 'number'))}
            options={requestedFileSelectOptions}
            placeholder={isLoadingRequestedFiles ? 'Loading Requests' : 'Select Requests'}
            searchPlaceholder="Find a Request"
            searchTestId={FIRM_DOCUMENT_EDIT_DIALOG_TEST_IDS.requestSearch}
            selectTestId={FIRM_DOCUMENT_EDIT_DIALOG_TEST_IDS.requestSelect}
            selectedPlacement="list"
            selectedIds={requestedFileIds}
            selectedOptionTestId={id => FIRM_DOCUMENT_EDIT_DIALOG_TEST_IDS.requestSelectedOption(Number(id))}
            testIdForOption={id => FIRM_DOCUMENT_EDIT_DIALOG_TEST_IDS.requestOption(Number(id))}
            triggerVariant="addButton"
          />
        </FirmDocumentFolderCreateDialogFieldGroup>
      </DialogContent>
      <DialogActionFooter
        actionButtonTestId={FIRM_DOCUMENT_EDIT_DIALOG_TEST_IDS.saveButton}
        actionLabel="Save Document"
        cancelButtonTestId={FIRM_DOCUMENT_EDIT_DIALOG_TEST_IDS.cancelButton}
        isActionDisabled={!canSave}
        isActionLoading={isSaving}
        isCancelDisabled={isSaving}
        onAction={handleConfirm}
        onCancel={onClose}
      />
    </Dialog>
  );
};

export default FirmDocumentEditDialog;
